import React, { useState, useEffect, useRef } from "react";
import { Check, X, PencilLine } from "@phosphor-icons/react";
import { Spinner, TextInput } from "flowbite-react";

interface InlineEditProps {
  value: string | number;
  onSave: (newValue: string) => Promise<void>;
  label?: string;
  prefix?: string;
  suffix?: string;
  type?: "text" | "number";
  className?: string;
  editIconPlacement?: "start" | "end";
  alwaysShowEdit?: boolean;
}

const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  prefix,
  suffix,
  type = "text",
  className = "",
  editIconPlacement = "end",
  alwaysShowEdit = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (inputValue === value.toString()) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      await onSave(inputValue);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save inline edit:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setInputValue(value.toString());
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <TextInput
          ref={inputRef}
          type={type}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          sizing="sm"
          className="w-32"
          icon={() => <span>{prefix}</span>}
        />
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <Spinner size="xs" />
          ) : (
            <Check size={16} weight="bold" />
          )}
        </button>
        <button
          onClick={() => {
            setInputValue(value.toString());
            setIsEditing(false);
          }}
          disabled={isLoading}
          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700/50 px-2 py-1 -mx-2 rounded-md transition-colors ${className}`}
      onClick={() => setIsEditing(true)}
    >
      {editIconPlacement === "start" && (
        <PencilLine
          size={14}
          className={`text-gray-400 dark:text-gray-500 transition-opacity ${alwaysShowEdit ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        />
      )}
      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
        {prefix}
        {value}
        {suffix}
      </span>
      {editIconPlacement === "end" && (
        <PencilLine
          size={14}
          className={`text-gray-400 dark:text-gray-500 transition-opacity ${alwaysShowEdit ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        />
      )}
    </div>
  );
};

export default InlineEdit;
